import React, { useState, useEffect } from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import {
  Box,
  InputAdornment,
  Tooltip,
  MenuItem,
  TextField,
  Button,
  Typography,
  IconButton,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import humbbleIcon from "../../../../../assets/AccountingModule/humbleicons_upload.svg";
import { QuoteValidation } from "../../../../../helper/Validation";
import color from "../../../../../constants/color";
import styles from "./style";

const NewQuote = ({
  customerList,
  itemList,
  loading,
  createQuote,
}) => {
  const [quoteDate, setQuoteDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [items, setItems] = useState([
    {
      name: "",
      itemId: "",
      description: "",
      invoiceQuantity: "",
      rate: "",
      amount: "",
    },
  ]);
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [filePreview, setFilePreview] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({
    customer: "",
    quoteNumber: "",
    referenceNumber: "",
    quoteDate: "",
    expiryDate: "",
    salesperson: "",
    subject: "",
    items: [{
      item: "",
      description: "",
      quantity: "",
      rate: "",
    }],
    customerNotes: "",
    termsAndConditions: "",
    discount: "",
    tax: "",
  });
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        itemId: "",
        description: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

    setErrors(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: "",
          description: "",
          quantity: "",
          rate: "",
        }
      ]
    }));
  };

  const isFormValid = () => {
    if (!customerId) return false;
    if (!quoteNumber.trim()) return false;
    if (!quoteDate) return false;
    if (!expiryDate) return false;

    if (items.length === 0) return false;

    const itemsValid = items.every(item =>
      item.name &&
      item.description &&
      item.invoiceQuantity &&
      item.rate &&
      item.amount
    );

    if (!itemsValid) return false;

    const hasErrors =
      errors.customer ||
      errors.quoteNumber ||
      errors.quoteDate ||
      errors.expiryDate ||
      errors.items.some(item => item.item || item.invoiceQuantity || item.rate);

    return !hasErrors;
  };

  // Add this useEffect to update form validity when dependencies change
  useEffect(() => {
    const formIsValid = isFormValid();
    // You might want to set this to a state variable if you're using it for UI
  }, [customerId, quoteNumber, quoteDate, expiryDate, items, errors]);

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);

      const updatedErrors = { ...errors };
      updatedErrors.items.splice(index, 1);
      setErrors(updatedErrors);
    }
  };

  const validateField = (fieldName, value, index = null) => {
    const rule = QuoteValidation[fieldName];

    if (!rule) return ""; // No rule defined, consider valid

    // Check if required field is empty
    if (rule.required && !value) {
      return rule.validator.errEmpty;
    }

    // Skip validation for empty non-required fields
    if (!rule.required && !value) {
      return "";
    }

    // Special validation for dates
    if (fieldName === 'expiryDate' && quoteDate && value && value < quoteDate) {
      return "Expiry date must be after quote date";
    }

    // Validate against regex pattern
    if (value && !rule.validator.regEx.test(value)) {
      return rule.validator.error;
    }

    return ""; // No error
  };

  const handleFormChange = (field, value) => {
    const error = validateField(field, value);

    setErrors(prev => ({
      ...prev,
      [field]: error
    }));

    // Update the corresponding state
    switch (field) {
      case 'quoteNumber':
        setQuoteNumber(value);
        break;
      case 'referenceNumber':
        setReferenceNumber(value);
        break;
      case 'subject':
        setSubject(value);
        break;
      case 'salesperson':
        setSalesperson(value);
        break;
      case 'customerNotes':
        setCustomerNotes(value);
        break;
      case 'termsAndConditions':
        setTermsAndConditions(value);
        break;
      case 'discount':
        setDiscountPercentage(Number(value) || 0);
        break;
      case 'tax':
        setTaxPercentage(Number(value) || 0);
        break;
      default:
        break;
    }
  };

  const handleItemChange = (value, index, key) => {
    const updatedItems = [...items];
    const updatedErrors = { ...errors };

    if (key === "quantity" || key === "rate") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }

    // Validate the changed field
    if (key === "name") {
      const error = validateField("item", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        item: error
      };
    } else if (key === "description") {
      const error = validateField("description", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        description: error
      };
    } else if (key === "quantity") {
      const error = validateField("quantity", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        quantity: error
      };
    } else if (key === "rate") {
      const error = validateField("rate", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        rate: error
      };
    }

    // Recalculate amount if quantity or rate changes
    if (key === "quantity" || key === "rate") {
      const qty = Number(updatedItems[index].quantity) || 0;
      const rate = Number(updatedItems[index].rate) || 0;
      updatedItems[index].amount = qty * rate;
    }

    if (key === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedItems[index].itemId = selectedItem._id || "";
        updatedItems[index].rate = Number(selectedItem.sellingPrice || 0);
        updatedItems[index].quantity = Number(selectedItem.quantity || 1);
        updatedItems[index].invoiceQuantity = 0;
        updatedItems[index].description = selectedItem.description || "";
        updatedItems[index].amount = (selectedItem.sellingPrice || 0) * (selectedItem.invoiceQuantity || 1);

        // Validate the auto-filled fields
        const quantityError = validateField("quantity", updatedItems[index].quantity.toString());
        const rateError = validateField("rate", updatedItems[index].rate.toString());

        updatedErrors.items[index] = {
          ...updatedErrors.items[index],
          quantity: quantityError,
          rate: rateError
        };
      }
    }

    setItems(updatedItems);
    calculateSubTotal();
    setErrors(updatedErrors);
  };

  const calculateSubTotal = () => {
    const total = items.reduce((sum, item) => {
      const amount =
        (Number(item.invoiceQuantity) || 0) * (Number(item.rate) || 0);
      return sum + amount;
    }, 0);
    return Number(total.toFixed(2)) || 0;
  };

  const calculateDiscountValue = () => {
    const subTotal = calculateSubTotal();
    const discount = (subTotal * (Number(discountPercentage) || 0)) / 100;
    return Number(discount.toFixed(2)) || 0;
  };

  const calculateTaxValue = () => {
    const subTotal = calculateSubTotal();
    const discountedAmount = subTotal - calculateDiscountValue();
    const tax = (discountedAmount * (Number(taxPercentage) || 0)) / 100;
    return Number(tax.toFixed(2)) || 0;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const total = subTotal - discountValue + taxValue;
    return Number(total.toFixed(2)) || 0;
  };

  const handleSubmit = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const grandTotal = calculateGrandTotal();

    const payload = {
      customer: customerId,
      quoteNumber,
      referenceNumber,
      quoteDate: quoteDate ? dayjs(quoteDate).format("YYYY-MM-DD") : null,
      expiryDate: expiryDate ? dayjs(expiryDate).format("YYYY-MM-DD") : null,
      salesperson,
      subject,
      items: items.map((item) => ({
        itemId: item.itemId,
        name: item.name,
        description: item.description,
        quantity: item.invoiceQuantity,
        rate: item.rate,
        amount: (item.rate || 0) * (item.invoiceQuantity || 0),
      })),
      customerNotes,
      total: subTotal,
      discountPercentage,
      discountValue,
      taxPercentage,
      taxValue,
      grandTotal,
      termsAndConditions,
      attachmentUrl: "https://yourdomain.com/uploads/quote-103.pdf", // Pass the selected file directly instead of URL
    };
    createQuote(payload);
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-error fieldset': {
        borderColor: 'red',
        borderWidth: '2px',
      },
    },
  };


  return (
    <>
      <Box px="20px" mt={2}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>New Quote</Typography>
        </Box>
      </Box>
      <Box sx={styles.container}>
        {/* Form Section */}
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            {/* First Row */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Customer Name</Typography>
                <Autocomplete
                  id="customer-select"
                  options={[{ _id: "add_new", name: "+ New Customer" }, ...(customerList || [])]}
                  getOptionLabel={(option) => option?.name || ""}
                  value={customer}
                  onChange={(event, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewCustomer"); // redirect page
                      return;
                    }
                    setCustomer(newValue);
                    setCustomerId(newValue?._id || "");
                    const error = validateField("customer", newValue?._id || "");
                    setErrors(prev => ({ ...prev, customer: error }));
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Customer"
                      size="small"
                      sx={{ ...styles.textField, ...textFieldStyles }}
                      error={!!errors.customer}
                      helperText={errors.customer}
                      onBlur={() => {
                        if (!customerId) {
                          const error = validateField("customer", "");
                          setErrors(prev => ({ ...prev, customer: error }));
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Quote#</Typography>
                <TextField
                  fullWidth
                  placeholder="Quote"
                  size="small"
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  onChange={(e) => handleFormChange('quoteNumber', e.target.value)}
                  error={!!errors.quoteNumber}
                  helperText={errors.quoteNumber}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Reference#</Typography>
                <TextField
                  fullWidth
                  placeholder="Reference"
                  onChange={(e) => handleFormChange('referenceNumber', e.target.value)}
                  size="small"
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  error={!!errors.referenceNumber}
                  helperText={errors.referenceNumber}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Quote Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      ...styles.datePickerOuter,
                    }}
                  >
                    <DatePicker
                      value={quoteDate}
                      onChange={(newValue) => {
                        setQuoteDate(newValue);
                        const error = validateField("quoteDate", newValue);
                        setErrors(prev => ({ ...prev, quoteDate: error }));

                        // Revalidate expiry date if quote date changes
                        if (expiryDate) {
                          const expiryError = validateField("expiryDate", expiryDate);
                          setErrors(prev => ({ ...prev, expiryDate: expiryError }));
                        }
                      }}
                      inputFormat="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            ...styles.datePicker,
                            ...textFieldStyles,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                              overflow: "hidden",
                            },
                          }}
                          error={!!errors.quoteDate}
                          helperText={errors.quoteDate}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Expiry Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={styles.datePickerOuter}>
                    <DatePicker
                      value={expiryDate}
                      onChange={(newValue) => {
                        setExpiryDate(newValue);
                        const error = validateField("expiryDate", newValue);
                        setErrors(prev => ({ ...prev, expiryDate: error }));
                      }}
                      inputFormat="DD/MM/YYYY"
                      minDate={quoteDate || null}
                      disabled={!quoteDate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            ...styles.datePicker,
                            ...textFieldStyles,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                              overflow: "hidden",
                            },
                          }}
                          error={!!errors.expiryDate}
                          helperText={errors.expiryDate}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Salesperson</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  value={salesperson}
                  placeholder="Select Salesperson"
                  onChange={(e) => handleFormChange('salesperson', e.target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected)
                        return (
                          <span style={{ color: "#999" }}>
                            Select Salesperson
                          </span>
                        );
                      return selected === "64f27b8e1234567890abcdef"
                        ? "John Doe"
                        : "Jane Smith";
                    },
                  }}
                >
                  <MenuItem value="64f27b8e1234567890abcdef">John Doe</MenuItem>
                  <MenuItem value="64f27b8e1234567890abcddf">
                    Jane Smith
                  </MenuItem>
                </TextField>
              </Box>
            </Grid>

            {/* Third Row */}
            <Grid item xs={12}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Subject</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter subject"
                  size="small"
                  onChange={(e) => handleFormChange('subject', e.target.value)}
                  sx={{
                    ...styles.multilineTextField,
                    ...textFieldStyles,
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.subject}
                  helperText={errors.subject}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={12}>
              <Box sx={styles.customerNotes}>
                <Typography>Customer Notes</Typography>
                <TextField
                  fullWidth
                  placeholder="Customer Notes"
                  onChange={(e) => handleFormChange('customerNotes', e.target.value)}
                  sx={{
                    ...styles.multilineTextField,
                    ...textFieldStyles, mt: 1,
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.customerNotes}
                  helperText={errors.customerNotes}
                />
                {/* </Box> */}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Items Section */}
      <Box sx={styles.container}>
        <Box sx={styles.itemsSection}>
          {items.map((item, index) => {
            const itemErrors = errors.items[index] || {};
            const stockQuantity =
              itemList.find((p) => p._id === item.itemId)?.quantity || 0;
            return (
              <Box key={index} sx={styles.itemRow}>
                {/* <img src={six_dot_icon} alt="drag" style={styles.sixDotsIcon} /> */}
                <Autocomplete
                  id={`item-select-${index}`}
                  options={[{ _id: "add_new", title: "+ New Item" }, ...(itemList || [])]}
                  getOptionLabel={(option) => option?.title || ""}
                  value={itemList?.find((i) => i.title === item.name) || null}
                  onChange={(event, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewItem"); // redirect page
                      return;
                    }
                    if (!newValue) {
                      handleItemChange("", index, "name");
                      handleItemChange("", index, "invoiceQuantity");
                      return;
                    }
                    handleItemChange(newValue?.title || "", index, "name");
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Item"
                      size="small"
                      sx={{
                        ...styles.itemField,
                        ...textFieldStyles,
                        flex: 1,
                        minWidth: 0,
                        "& .MuiInputBase-root": {
                          height: "100%",
                          fontSize: "1rem",
                        },
                      }}
                      error={!!itemErrors.item}
                      helperText={itemErrors.item}
                    />
                  )}
                />
                <TextField
                  size="small"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(e.target.value, index, "description")
                  }
                  sx={{
                    ...styles.descriptionField,
                    ...textFieldStyles,
                    overflow: "hidden",
                    display: "flex",
                    textarea: {
                      resize: "none",
                      overflow: "hidden",
                    },
                    minWidth: "320px",
                    height: 43,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                  }}
                  error={!!itemErrors.description}
                  helperText={itemErrors.description}
                />

                <Grid item xs={12} lg={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ ...styles.customInput, width: "100%" }}
                  >
                    <TextField
                      value={items[index]?.invoiceQuantity}// Show empty string when 0
                      type="number"
                      placeholder="Quantity"
                      sx={{ ...styles.quality, ...textFieldStyles }}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const maxQuantity = items[index]?.quantity;
                        if (
                          !isNaN(value) &&
                          value >= 0 &&
                          value <= maxQuantity
                        ) {
                          handleItemChange(value, index, "invoiceQuantity");
                        } else if (e.target.value === "") {
                          handleItemChange("", index, "invoiceQuantity");
                        } else {
                          const validValue = value < 0 ? 0 : maxQuantity;
                          handleItemChange(
                            validValue,
                            index,
                            "invoiceQuantity"
                          );
                        }
                      }}
                      error={!!itemErrors.quantity}
                      helperText={itemErrors.quantity}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" variant="filled">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                bgcolor: "#DEDEDE",
                                height: 41,
                                p: 0,
                                ...styles.inputBgBox,
                                pl: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 21,
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "end",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  borderRadius: "8px",
                                }}
                              >
                                {stockQuantity}
                              </Box>
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Box
                                    sx={{
                                      bgcolor: "white",
                                      p: 1,
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        color: "#373D3F",
                                        mb: 1,
                                      }}
                                    >
                                      Stock Details
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        color: "#373D3F",
                                        fontWeight: 400,
                                      }}
                                    >
                                      Stock on Hand :{" "}
                                      {stockQuantity || "0"}
                                    </Typography>
                                  </Box>
                                }
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "white !important",
                                      p: 0,
                                      border: "1px solid #878787 !important",
                                      boxShadow: "none !important",
                                      "& .MuiTooltip-arrow": {
                                        color: "white !important",
                                        "&::before": {
                                          border: "1px solid #878787",
                                          backgroundColor: "white !important",
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  size="small"
                                  edge="end"
                                  sx={{
                                    color: "#878787",
                                  }}
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <TextField
                  size="small"
                  type="number"
                  placeholder="Rate"
                  value={
                    items[index]?.rate === "" ? "" : String(items[index]?.rate)
                  }
                  onChange={(e) =>
                    handleItemChange(e.target.value, index, "rate")
                  }
                  sx={{
                    ...styles.rateField,
                    ...textFieldStyles,
                    flex: 1,
                    minWidth: 0,
                    height: 43,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                  }}
                  error={!!itemErrors.rate}
                  helperText={itemErrors.rate}
                />
                <TextField
                  size="small"
                  placeholder="Amount"
                  value={
                    (items[index]?.rate || 0) *
                    (items[index]?.invoiceQuantity || 0) || ""
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    ...styles.amountField,
                    flex: 1, // Stretch proportionally
                    minWidth: 0, // Prevent overflow
                    height: 43, // outer height of the text field
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: "12px 14px", // control vertical padding for input
                      boxSizing: "border-box",
                    },
                  }}
                />

                {index === 0 ? (
                  <IconButton onClick={handleAddItem} sx={styles.addButton}>
                    <AddIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => handleRemoveItem(index)}
                    sx={styles.deleteButton}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Summary Section */}
      <Grid container spacing={0}>
        <Grid item xs={12} md={7}>
          <Box sx={styles.container4}>
            <Box
              sx={{
                height: "140px",
                backgroundColor: "white",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#202020",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  "&::before": {
                    content: '""',
                    width: "3px",
                    height: "16px",
                    backgroundColor: "#28AA4A",
                    marginRight: "8px",
                    borderRadius: "2px",
                  },
                }}
              >
                Discount and Tax (%)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                    >
                      Discount
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Discount"
                      value={discountPercentage || ""}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0 && value <= 100) {
                          setDiscountPercentage(value);
                        } else if (e.target.value === "") {
                          setDiscountPercentage(0);
                        }
                      }}
                      inputProps={{ min: 0, max: 100 }}
                      sx={{
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                    >
                      Tax
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Tax"
                      value={taxPercentage || ""}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0 && value <= 100) {
                          setTaxPercentage(value);
                        } else if (e.target.value === "") {
                          setTaxPercentage(0);
                        }
                      }}
                      inputProps={{ min: 0, max: 100 }}
                      sx={{
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={styles.container3}>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                height: "140px",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>Total</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${calculateSubTotal().toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>
                  Tax ({taxPercentage || 0}%)
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${calculateTaxValue().toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>
                  Discount ({discountPercentage || 0}%)
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  -${calculateDiscountValue().toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  pt: 1,
                  borderTop: "1px solid #eee",
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>Grand Total</Typography>
                <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                  ${calculateGrandTotal().toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {/* </Box> */}

      {/* Attachment Section */}
      <Box sx={styles.container}>
        <Grid container spacing={2.5} sx={styles.attachmentSection}>
          {/* Terms & Conditions Section - Left Side */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.textAreaBox}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Terms & Conditions
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter terms and conditions"
                onChange={(e) => setTermsAndConditions(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    height: "76px",
                    borderRadius: "8px !important",
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Attach File Section - Right Side */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.fileBox}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Attach File to Quote
              </Typography>
              <Box
                sx={{
                  ...styles.fileDropArea,
                  border: "2px dashed #ccc",
                  borderRadius: "8px !important",
                  p: 2,
                  minHeight: "76px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "#28AA4A",
                  },
                }}
              >
                <input
                  type="file"
                  id="quoteFileUpload"
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="quoteFileUpload"
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {filePreview ? (
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                      {selectedFile?.type.startsWith("image/") ? (
                        <img
                          src={filePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "60px",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                          <Typography>{selectedFile?.name}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={
                          <Box
                            component="span"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <img
                              src={humbbleIcon}
                              alt="Upload"
                              style={{ width: "16px", height: "16px" }}
                            />
                          </Box>
                        }
                        sx={{
                          backgroundColor: "#28AA4A",
                          color: "white",
                          textTransform: "none",
                          height: "36px",
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor: "#229A41",
                          },
                        }}
                      >
                        Choose File
                      </Button>
                      <Typography sx={{ color: "#666" }}>
                        or drag and drop file here
                      </Typography>
                    </Box>
                  )}
                </label>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={styles.actionButtons}>
        <Button
          variant="contained"
          sx={styles.draftButton}
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
        >
          Save
        </Button>
        {/* <Button
            variant="contained"
            sx={styles.saveButton}
            onClick={handleSaveAndPublish}
            disabled={!isFormValid || loading}
          >
            Save and Publish
          </Button> */}
        <Button variant="outlined" sx={styles.cancelButton}>
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default NewQuote;
